import {
  ComponentProps,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Input, Row } from 'antd';
import cc from 'classcat';
import { deviceActionManager } from 'deviceactionmanager/DeviceActionManager';
import { ColSizes } from 'utils/constants/app/ui';
import { BETSLIP_STAKE_LIMIT, SEPARATOR } from 'utils/constants/betslip';
import { BET_BUILDER } from 'utils/constants/betslip/bet-types';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { CommandNames } from 'utils/constants/swarm/swarm-command-names';
import { fireEvent } from 'utils/generic/event-bus';
import { lazy } from 'utils/generic/lazy';
import { removeComma } from 'utils/helpers/validators';
import { isMobile } from 'utils/is-mobile';
import { BookingIds } from 'interfaces/bet-data';
import { swarmCommand } from 'services/get-swarm-data';
import { BetslipCalculationResult } from 'newelements/_Betslip/components/BetslipCalculationResult';
import { useBookBet } from 'newelements/_Betslip/hooks/useBookBet';
import { roundAmount } from 'newelements/_Betslip/utils/roundings/round-amount';
import { Container } from 'components/GameNameRedirection';
import { EPlayerActivityCheckerActions } from 'newcomponents/Shared/account/PlayerActivityChecker/types';
import { BetBuilderStakeInput } from 'newcomponents/Shared/sportsbook/BetBuilderModal/components/BetBuilderStakeInput';
import { UseGenerateBetBuilderFooterFields } from 'newcomponents/Shared/sportsbook/BetBuilderModal/hooks/useGenerateBetBuilderFooterFields';
import { IBetBuilderMessage } from 'newcomponents/Shared/sportsbook/BetBuilderModal/types';
import { Button } from 'newcomponents/UI/Button';
import { Divider } from 'newcomponents/UI/Divider';
import {
  priceTypes,
  TPriceTypesKeys,
  useGenerateBetBuilderUrl
} from 'hooks/sportsbook/useGenerateBetBuilderUrl';
import { useCheckStakeBalance } from 'hooks/useCheckStakeBalance';
import { useCurrency } from 'hooks/useCurrency';
import { updateOpenBets } from 'store/actions';
import { getLeftSidebarOpenCloseTrigger } from 'store/selectors/app-data';
import { getOddFormat, getUseBonusBalance } from 'store/selectors/betslip';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';
import './index.less';

const { BookingModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "betslip-booking-modal" */ 'newelements/_Betslip/layouts/Basic/components/Features/BookingBet/components/BookingModal'
    )
);

const { BetBuilderSuccessBetModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "BetBuilderSuccessBetModal" */ 'newcomponents/Shared/sportsbook/BetBuilderModal/components/BetBuilderSuccessBetModal'
    )
);

type TBetBuilderContentProps = {
  fixtureId: number;
  gameTitle?: string;
  sportId?: number;
  showTitle?: boolean;
  scale?: boolean;
};

const SCALABLE_IFRAME_MIN_WIDTH = 600;
const BB_IFRAME_WRAPPER_ID = 'bet-builder-wrapper';

export const BetBuilderContent = ({
  gameTitle,
  fixtureId,
  sportId,
  showTitle = true,
  scale
}: TBetBuilderContentProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isLoggedIn = useSelector(getIsLoggedIn);
  const useBonusBalance = useSelector(getUseBonusBalance);
  const user = useSelector(getUser);
  const oddFormat = useSelector(getOddFormat) as TPriceTypesKeys;

  const leftSidebarOpenCloseTrigger = useSelector(
    getLeftSidebarOpenCloseTrigger
  );

  const [scaleValue, setScaleValue] = useState(1);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [bookIds, setBookIds] = useState<BookingIds>([]);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [placeBetLoading, setPlaceBetLoading] = useState(false);
  const [bookBetLoading, setBookBetLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [stakeAmount, setStakeAmount] = useState('');
  const [decimalPrice, setDecimalPrice] = useState<number>(0);
  const [betslipUid, setBetslipUid] = useState('');
  const [betBuilderData, setBetBuilderData] = useState<string[]>([]);
  const [priceList, setPriceList] = useState<{
    [key: string]: string | number;
  }>({});

  const { currency, placement } = useCurrency();
  const checkStakeBalance = useCheckStakeBalance();
  const betBuilderUrl = useGenerateBetBuilderUrl({ fixtureId });
  const fields = UseGenerateBetBuilderFooterFields(
    +stakeAmount,
    +decimalPrice,
    sportId
  );

  const { onBookBet } = useBookBet({
    setBookingModalVisible,
    setBookIds: setBookIds,
    currentType: BET_BUILDER.NAME,
    totalStake: +stakeAmount,
    setSystemMinCount: () => {},
    betslipRef: { current: null },
    betslipUid: betslipUid
  });

  const resetBetslip = () => {
    setBetBuilderData([]);
    setBetslipUid('');
    setPriceList({});
    setDecimalPrice(0);
    changeStakeAmount('');
  };

  const changeStakeAmount = useCallback(
    (value: string) => {
      if (Number(removeComma(value)) >= 0) {
        setStakeAmount(roundAmount(removeComma(value)));
      }
    },
    [isLoggedIn, useBonusBalance, user]
  );

  const handlePlaceBet = useCallback(() => {
    const amount = stakeAmount === '' ? '0' : stakeAmount;

    checkStakeBalance('bet-builder-wrapper', amount, (disable: boolean) => {
      if (!(user.balance < Number(amount) || disable)) {
        setPlaceBetLoading(true);

        swarmCommand(
          CommandNames.SPORT_CAST_CREATE_BET,
          {
            betslip_uid: betslipUid,
            amount: stakeAmount
          },
          () => {
            if (SpringConfigs.WRAPPER_APP && window.median) {
              deviceActionManager.vibrate('impactMedium');
            }

            fireEvent({
              key: EPlayerActivityCheckerActions.checkPlayerActivity
            });

            setPlaceBetLoading(false);
            setIsSuccessModalOpen(true);
            dispatch(updateOpenBets());
          },
          () => null,
          () => {
            setPlaceBetLoading(false);
          }
        );
      }
    });
  }, [betslipUid, stakeAmount]);

  const inputDynamicProps = useMemo<
    Pick<ComponentProps<typeof Input>, 'suffix' | 'prefix' | 'maxLength'>
  >(
    () => ({
      [placement === 'left' ? 'prefix' : 'suffix']: <span>{currency}</span>,
      maxLength: SEPARATOR ? BETSLIP_STAKE_LIMIT + 2 : BETSLIP_STAKE_LIMIT
    }),
    [placement, currency]
  );

  const handleBookBet = () => {
    setBookBetLoading(true);
    betslipUid && onBookBet();
  };

  const handleSuccessModalAction = useCallback(() => {
    resetBetslip();
    setIsSuccessModalOpen(false);
  }, [resetBetslip]);

  useEffect(() => {
    const handler = (event: { data: string }) => {
      try {
        const parsedData = JSON.parse(event.data) as IBetBuilderMessage;

        if (parsedData?.betslipUid) {
          setBetslipUid(parsedData.betslipUid);
        }

        if (parsedData?.betBuilderData?.Selection) {
          setBetBuilderData(parsedData.betBuilderData.Selection);
        }

        if (parsedData?.betBuilderData?.PriceDetails?.DecimalPrice) {
          setPriceList(parsedData?.betBuilderData?.PriceDetails);
          setDecimalPrice(
            parsedData?.betBuilderData?.PriceDetails?.DecimalPrice
          );
        }
      } catch (e) {
        return;
      }
    };

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    checkStakeBalance(
      'bet-builder-wrapper',
      stakeAmount,
      (disable: boolean) => {
        setSubmitDisabled(
          stakeAmount === '' || user.balance < Number(stakeAmount) || disable
        );
      }
    );
  }, [stakeAmount, user.balance, useBonusBalance]);

  useEffect(() => {
    bookingModalVisible && setBookBetLoading(false);
  }, [bookingModalVisible]);

  useEffect(() => {
    if (scale) {
      const containerWidth =
        document.getElementById(BB_IFRAME_WRAPPER_ID)?.clientWidth || 0;

      if (containerWidth < SCALABLE_IFRAME_MIN_WIDTH) {
        const scaleValue = containerWidth / SCALABLE_IFRAME_MIN_WIDTH;

        setScaleValue(scaleValue);
      }
    }
  }, [leftSidebarOpenCloseTrigger]);

  return (
    <>
      {bookingModalVisible && (
        <Container>
          <Suspense fallback={null}>
            <BookingModal
              visible
              noSocialSharing
              bookIds={bookIds}
              onCancel={() => setBookingModalVisible(false)}
            />
          </Suspense>
        </Container>
      )}
      {isSuccessModalOpen && (
        <Suspense>
          <BetBuilderSuccessBetModal
            handleSuccessModalAction={handleSuccessModalAction}
          />
        </Suspense>
      )}
      <div
        id={BB_IFRAME_WRAPPER_ID}
        className="bbIframeWrapper"
        onClick={e => {
          e.stopPropagation();
        }}
      >
        {showTitle && (
          <div className="bbIframeWrapper__header">
            <span>{`${t('sportsbook.betBuilder')} | ${gameTitle}`}</span>
          </div>
        )}
        <div className="bbIframeWrapper__inner">
          {SpringConfigs.BET_BUILDER_URL ? (
            <iframe
              id="bbIframe"
              style={{
                border: 0,
                ...(scale && {
                  minWidth: SCALABLE_IFRAME_MIN_WIDTH,
                  transform: `scale(${scaleValue})`,
                  transformOrigin: 'top left'
                })
              }}
              height={isMobile() ? '764px' : '721px'}
              width="100%"
              src={betBuilderUrl}
            />
          ) : (
            <div className="bbIframeWrapper__empty">
              {t('generic.iframeError')}
            </div>
          )}
          {betslipUid && (
            <>
              <div className="bbDisableOverlay" />
              <div
                className={cc([
                  'bbBetslip',
                  { 'bbBetslip--mobile': isMobile() }
                ])}
              >
                <div className="bbBetslip__remove" onClick={resetBetslip}>
                  <span>{t('generic.removeAll')}</span>
                </div>
                <Row className="bbBetslip__body">
                  <Col
                    xl={ColSizes.SIZE_12}
                    xs={ColSizes.SIZE_24}
                    className={cc([
                      'bbBetslip__body__left',
                      { 'bbBetslip__body__left--mobile': isMobile() }
                    ])}
                  >
                    <div className="bbBetslip__body__left__header">
                      <span>
                        <span className="bbBetslip__body__left__header__count">
                          {betBuilderData?.length || 0}
                        </span>
                        <span className="bbBetslip__body__left__header__title">
                          {t('betslip.betslip')}
                        </span>
                      </span>
                      <span className="bbBetslip__body__left__header__odd">
                        {t('account.odds')}:{' '}
                        {priceList[priceTypes[oddFormat]] || decimalPrice}
                      </span>
                    </div>
                    {betBuilderData?.map(value => {
                      return (
                        <div
                          key={value}
                          className="bbBetslip__body__left__header__event"
                        >
                          <span>{value}</span>
                        </div>
                      );
                    })}
                  </Col>
                  <Col
                    xl={ColSizes.SIZE_12}
                    xs={ColSizes.SIZE_24}
                    className={cc([
                      'bbBetslip__body__right',
                      { 'bbBetslip__body__right--mobile': isMobile() }
                    ])}
                  >
                    <BetBuilderStakeInput
                      stakeAmount={stakeAmount}
                      changeStakeAmount={changeStakeAmount}
                      handlePlaceBet={handlePlaceBet}
                      inputDynamicProps={inputDynamicProps}
                      submitDisabled={submitDisabled}
                    />

                    {!isMobile() && <Divider distance={8} />}

                    {fields.map(field => (
                      <BetslipCalculationResult key={field.id} {...field} />
                    ))}

                    <div className="bbBetslip__body__right__submit">
                      {SpringConfigs.TERMINAL_MODE && (
                        <Button
                          type="cancel"
                          onClick={handleBookBet}
                          loading={bookBetLoading}
                        >
                          {t('account.getCode')}
                        </Button>
                      )}
                      <Button
                        type="primary"
                        onClick={handlePlaceBet}
                        disabled={submitDisabled}
                        loading={placeBetLoading}
                      >
                        {t('betslip.placeBet')}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
